import React from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, ButtonGroup, Heading, Paragraph, Text } from '@zillow/constellation-10';

import Page from '@renter-hub/components/RenterPage';
import NotificationContainer from '@common/components/NotificationContainer';
import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import HelmetContainer from '@common/components/HelmetContainer';
import TotalPaymentModule from '@renter-hub/components/TotalPaymentModule';
import SeoConstants from '@renter-hub/constants/seoConstants';
import userPersonas from '@common/constants/userPersonas';
import faqs from '@common/constants/faqs';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import { capitalizeFirstLetter } from '@common/utils/stringUtils';
import type { RenterPaymentMethodInterface } from '@renter-hub/models/Types';
import AvailablePaymentMethods from '../../components/AvailablePaymentMethods';
import RachelCSEvents from '@renter-hub/tracking/ClickStreamEvents';
import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';
import { useOneTimeRenterPaymentWebApiHandler_oneTimeRenterPayment } from '@zg-rentals/ts-rent-payment-client-react-query';
import type { OneTimeRenterPaymentWebApiRequestDto } from '@zg-rentals/ts-rent-payment-client';
import { logError } from '@zg-rentals/log-error';
import HTTP_STATUS from '@common/constants/HttpConstants';
import { checkRequestActionSuccess, getSignInPage } from '@common/paymentsUtils/api';
import { closeNotification, showPageNotification } from '@common/actions/NotificationActions';
import { push } from 'connected-react-router';
import { getRachelDashboard } from '@renter-hub/actions/requests';
import paths from '@renter-hub/pathsManifest';
import {
  DEFAULT_ERROR,
  ONE_TIME_PAYMENT_PAID,
  PAYMENT_PLAN_INSTALLMENT_PAID,
} from '@renter-hub/constants/notifications';

interface ReduxAction {
  type: string;
  payload?: unknown;
  error?: unknown;
}

interface PayOneTimePaymentPageProps {
  transaction: {
    amount: number;
    totalAmount: number;
    zgFee: number;
    paymentType: string;
    notes?: string;
    paymentPlanInstallmentTitle?: string;
  };
  accountLast4: string;
  accountName: string;
  landlordName: string;
  landlordFirstName: string;
  getPaymentPayload: (selectedPaymentMethodId: string) => OneTimeRenterPaymentWebApiRequestDto;
  failedPaymentEmail?: boolean;
  paymentFailureReason?: string;
  paymentMethods: Array<RenterPaymentMethodInterface>;
  user: ZrmClickstreamUser;
  paymentPlanInstallmentTitle?: string;
  baseUrl?: string;
  propertyId?: string | undefined;
}

const PayOneTimePaymentPageComponent: React.FC<PayOneTimePaymentPageProps> = (props) => {
  const dispatch = useDispatch();
  const [failedPayment, setFailedPayment] = React.useState(props.failedPaymentEmail || false);
  const [_paymentFailureReason, setPaymentFailureReason] = React.useState(props.paymentFailureReason || '');
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = React.useState(
    props.paymentMethods?.length > 0 ? props.paymentMethods[0]?.id || '' : '',
  );
  const [isLoading, setIsLoading] = React.useState(false);

  const showErrorNotification = () => dispatch(showPageNotification(DEFAULT_ERROR));
  const showSuccessNotification = (paymentPlanInstallmentTitle?: string) => {
    if (paymentPlanInstallmentTitle) {
      dispatch(showPageNotification(PAYMENT_PLAN_INSTALLMENT_PAID, { paymentPlanInstallmentTitle }));
    } else {
      dispatch(showPageNotification(ONE_TIME_PAYMENT_PAID));
    }
  };

  const { mutate: payOneTimePayment } = useOneTimeRenterPaymentWebApiHandler_oneTimeRenterPayment({
    onSuccess: async () => {
      try {
        const results = (await dispatch(
          getRachelDashboard({ propertyId: props.propertyId }),
        )) as unknown as Array<ReduxAction>;
        const [renterStatusResponse] = results ?? [];
        await checkRequestActionSuccess(renterStatusResponse);

        dispatch(push(props.baseUrl ?? paths.baseUrl));
        showSuccessNotification(props.paymentPlanInstallmentTitle);
      } catch (error) {
        logError({ error, context: 'PayOneTimePaymentPage - payOneTimePayment success flow' });
        showErrorNotification();
        setFailedPayment(true);
      }
    },
    onError: async (error) => {
      const err = error as { responseStatus?: number; message?: string; code?: string };

      logError({ error, context: 'PayOneTimePaymentPage - payOneTimePayment' });

      if (err.responseStatus === HTTP_STATUS.SC_UNAUTHORIZED) {
        window.location.href = getSignInPage();
        return;
      }

      if (err.responseStatus === HTTP_STATUS.SC_UNPROCESSABLE_ENTITY) {
        showErrorNotification();
        setFailedPayment(true);
        setPaymentFailureReason(err.code || err.message || '');
        return;
      }

      showErrorNotification();
      setFailedPayment(true);
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });

  const renderTotalPaymentModule = () => {
    const detailsOfSelectedPaymentMethod = props.paymentMethods.find(
      (paymentMethod) => paymentMethod.id === selectedPaymentMethodId,
    );

    const oneTimePaymentAmount = detailsOfSelectedPaymentMethod?.oneTimePaymentAmountDetails?.find(
      (oneTimeDetails) => oneTimeDetails.groupKey === props.getPaymentPayload(selectedPaymentMethodId).externalGroupKey,
    );

    if (oneTimePaymentAmount) {
      return <TotalPaymentModule totalAmount={oneTimePaymentAmount.totalAmount} feeAmount={oneTimePaymentAmount.fee} />;
    }

    const { totalAmount, zgFee } = props.transaction;
    if (Number(zgFee) === 0) {
      return null;
    }
    return (
      <Box>
        <TotalPaymentModule totalAmount={totalAmount} feeAmount={zgFee} />
      </Box>
    );
  };

  const onPaymentMethodChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    setSelectedPaymentMethodId(e.target.value);
  };

  const goToDashboard = () => {
    dispatch(closeNotification());
    dispatch(push(props.baseUrl ?? paths.baseUrl));
  };

  const renderButtons = () => {
    if (props.failedPaymentEmail) {
      return (
        <>
          <DebouncedButton emphasis="filled" size="lg" fluid={true} onClick={onSubmit} loading={isLoading}>
            Try again
          </DebouncedButton>
          <Paragraph textStyle="body-sm" css={{ textAlign: 'center', color: 'text.subtle' }}>
            Your payment will be processed immediately
          </Paragraph>
        </>
      );
    }
    return (
      <>
        <ButtonGroup aria-label="confirm-mark-paid" role="group">
          <Button size="lg" fluid={true} onClick={goToDashboard}>
            Back
          </Button>
          <DebouncedButton emphasis="filled" size="lg" fluid={true} onClick={onSubmit} loading={isLoading}>
            {failedPayment ? 'Try again' : 'Send now'}
          </DebouncedButton>
        </ButtonGroup>
        <Paragraph textStyle="body-sm" css={{ color: 'text.subtle', textAlign: 'center' }}>
          Your payment will be processed immediately
        </Paragraph>
      </>
    );
  };

  const onSubmit = async (): Promise<void> => {
    setIsLoading(true);
    dispatch(closeNotification());

    RachelCSEvents.emitClicksSendNowForOTPEvent({ user: props.user });

    const payload = props.getPaymentPayload(selectedPaymentMethodId);
    payOneTimePayment({ postData: payload });
  };

  return (
    <Page>
      <HelmetContainer {...SeoConstants.payOneTimePaymentPage} />
      <Page.MainSection>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'token(spacing.md)' }}>
          <NotificationContainer persona={userPersonas.RENTER} />
          <Heading level={1} textStyle="heading-sm">
            {props.landlordFirstName} has requested a payment
          </Heading>
          <Box>
            <Paragraph textStyle="body-bold">Amount</Paragraph>
            <Paragraph>{centsToDollars(props.transaction.amount, { style: 'effectiveShort' })}</Paragraph>
          </Box>
          <Box>
            <Paragraph textStyle="body-bold">For</Paragraph>
            <Paragraph>
              {props.transaction.paymentPlanInstallmentTitle || capitalizeFirstLetter(props.transaction.paymentType)}
            </Paragraph>
            {props.transaction.notes && <Paragraph>{props.transaction.notes}</Paragraph>}
          </Box>
          <Box>
            <Paragraph textStyle="body-bold">Pay To</Paragraph>
            <Text className="fs-exclude">{props.landlordName}</Text>
          </Box>
          <AvailablePaymentMethods
            renderSelect
            onOptionChange={onPaymentMethodChange}
            isLoading={isLoading}
            selectedPaymentMethodId={selectedPaymentMethodId}
          />

          {renderTotalPaymentModule()}
          {renderButtons()}
        </Box>
      </Page.MainSection>
      <Page.RightSection>
        <FaqComponent faqs={faqs.ONE_TIME_PAYMENT_RACHEL.body} />
      </Page.RightSection>
      <Page.BottomSection>
        <FaqComponent faqs={faqs.ONE_TIME_PAYMENT_RACHEL.body} />
      </Page.BottomSection>
    </Page>
  );
};

export default PayOneTimePaymentPageComponent;
